import React, { useContext, useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import Glide from "@glidejs/glide";
import { DocumentContext } from "~context/DocumentContext";
import ArrowTopLeft from "~components/svg/ArrowTopLeft";
import ArrowTopRight from "~components/svg/ArrowTopRight";

const Carousel = ({
  autoPlay,
  bulletColor,
  className,
  items,
  keyPrefix,
  onUpdate,
  peek,
  perView,
  withBullets
}) => {
  const { windowWidth } = useContext(DocumentContext);

  const glideRef = useRef();

  const [activeIndex, setActiveIndex] = useState(0);
  const [glide, setGlide] = useState(null);

  //

  const carouselNext = () => {
    if (!glide) {
      return;
    }

    glide.go(`<`);
  };

  const carouselPrev = () => {
    if (!glide) {
      return;
    }

    glide.go(`>`);
  };

  useEffect(() => {
    if (!glideRef?.current || !windowWidth || glide) {
      return;
    }

    const glideCarousel = new Glide(`#${keyPrefix}-glide`, {
      autoPlay: autoPlay <= 0 ? false : autoPlay,
      bound: perView !== 1,
      gap: 0,
      hoverpause: true,
      peek,
      perView,
      rewind: false,
      type: `carousel`
    });

    glideCarousel.on(`run`, () => {
      setActiveIndex(glideCarousel.index);

      onUpdate(glideCarousel.index);
    });

    glideCarousel.mount();

    setGlide(glideCarousel);
  }, [glideRef, windowWidth]);

  //

  return (
    <section
      key={`${keyPrefix}-container`}
      className={`carousel ${className ||
        `w-full relative`} flex items-center justify-center overflow-x-hidden`}
    >
      <div className="w-full absolute top-0 right-0 bottom-0 left-0 z-10 pointer-events-none flex justify-between">
        <button
          type="button"
          className="h-full relative block md:px-8 cursor-pointer pointer-events-auto"
          onClick={carouselNext}
          style={{
            marginLeft: `${windowWidth * 0.05}px`
          }}
        >
          <ArrowTopLeft className="w-10" />
        </button>

        <button
          type="button"
          className="h-full relative block md:px-8 cursor-pointer pointer-events-auto"
          onClick={carouselPrev}
          style={{
            marginRight: `${windowWidth * 0.05}px`
          }}
        >
          <ArrowTopRight className="w-10" />
        </button>
      </div>

      <div className="carousel__glide w-full h-full relative block">
        <div
          id={`${keyPrefix}-glide`}
          ref={glideRef}
          className="glide h-full relative"
        >
          <div
            className="glide__track w-full h-full relative"
            data-glide-el="track"
          >
            <ul className="glide__slides h-full relative">
              {items.map((itemJSX, itemIndex) => {
                const key = `${keyPrefix}-item-${itemIndex}`;

                return (
                  <li key={key} className="glide__slide relative">
                    {itemJSX}
                  </li>
                );
              })}
            </ul>
          </div>

          {withBullets && (
            <div
              className="glide__bullets relative mt-v6 mb-v6 flex items-center justify-center"
              data-glide-el="controls[nav]"
            >
              {items.map((_, itemIndex) => {
                const bulletKey = `${keyPrefix}-bullet-${itemIndex}`;

                if (perView !== 1 && itemIndex === items.length - 1) {
                  return <div key={bulletKey} className="hidden"></div>;
                }

                return (
                  <button
                    key={bulletKey}
                    type="button"
                    className={`glide__bullet mr-v05 ml-v05 ${
                      activeIndex === itemIndex ? `` : `opacity-50`
                    } bg-${bulletColor}`}
                    data-glide-dir={`=${itemIndex}`}
                  ></button>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </section>
  );
};

Carousel.defaultProps = {
  autoPlay: 0,
  bulletColor: `white`,
  className: ``,
  keyPrefix: `glide-carousel`,
  onUpdate: () => {},
  peek: 0,
  perView: 1,
  withBullets: false
};

Carousel.propTypes = {
  autoPlay: PropTypes.number,
  bulletColor: PropTypes.string,
  className: PropTypes.string,
  items: PropTypes.node.isRequired,
  keyPrefix: PropTypes.string,
  onUpdate: PropTypes.func,
  peek: PropTypes.number,
  perView: PropTypes.number,
  withBullets: PropTypes.bool
};

export default Carousel;
